import { useCallback, useEffect } from 'react'
import queryString from 'query-string'
import styled from 'styled-components'
import AnimatedLoader from '../shared_components/AnimatedLoader'
import { Box } from '../shared_components/UI/Box'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { theseus } from '../api/theseus'
import { useDispatch } from 'react-redux'
import { setAccount } from '../modules/web3/actions'
import {
  setDiscordWarningNotice,
  setTwitterWarningNotice,
} from '../modules/miscUi/actions'
import { SocialDisconnectEvent } from '../hooks/useWeb3'
import { setLithiumAccount } from '../modules/user/slice'

export const FullPageContainer = styled(Box)`
  width: 100vw;
  height: 100vh;
  background: #000;
  z-index: 9999;
  position: fixed;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
`

export function useRefetchUpdateUser() {
  const dispatch = useDispatch()

  const refetchUpdateUser = useCallback(async () => {
    try {
      const res = await theseus.get('/auth/b2c/verify?refetch=true')
      if (res.status !== 200) throw new Error('No user')
      const {
        data: { user, userSocialDisconnectEvents },
      } = res
      const { walletAddress } = user ?? {}
      if (!walletAddress) throw new Error('No user')

      dispatch(setAccount(walletAddress))
      dispatch(setLithiumAccount(user))
      if (!!userSocialDisconnectEvents?.length) {
        const sortedSocialDisconnectEvents = userSocialDisconnectEvents.sort(
          (a: SocialDisconnectEvent, b: SocialDisconnectEvent) => {
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            )
          },
        )
        for (const {
          meta_data: { type, handle },
        } of sortedSocialDisconnectEvents) {
          if (type === 'twitter') {
            if (!!user?.twitterHandle) continue
            dispatch(
              setTwitterWarningNotice(
                `You were logged out of your X account (${handle}) due to inactivity, please connect again to resume functionality`,
              ),
            )
          }
          if (type === 'discord') {
            dispatch(
              setDiscordWarningNotice(
                `You were logged out of your Discord account (${handle}) due to inactivity, please connect again to resume functionality`,
              ),
            )
          }
        }
      }

      return true
    } catch (error) {
      console.error(error)

      return false
    }
  }, [dispatch])

  return refetchUpdateUser
}

const TwitterAuth = () => {
  const history = useHistory()
  const refetchUpdateUser = useRefetchUpdateUser()
  const verifyAndRedirect = useCallback(
    async (launchedFrom: string) => {
      try {
        const hasSuccessfullyRefetched = await refetchUpdateUser()
        if (!hasSuccessfullyRefetched) throw new Error('Failed to refetch user')
        toast.success('Twitter connected successfully')
        history.replace(launchedFrom, { skipHistoryUpdate: true })
      } catch (error) {
        console.error(error)
        toast.error('Failed to connect Twitter')
      }
    },
    [history, refetchUpdateUser],
  )

  useEffect(() => {
    const launchedFrom = localStorage.getItem('launched_from')
    const queries = queryString.parse(window.location.search)
    try {
      const { success, error } = queries
      if (success !== 'true') throw new Error(error as string)
      if (!launchedFrom) throw new Error('No launched from')
      localStorage.removeItem('launched_from')
      verifyAndRedirect(launchedFrom)
    } catch (err) {
      console.error(err)
      if (err.message) {
        toast.error(err.message)
      }
      history.replace(launchedFrom ?? '/', { skipHistoryUpdate: true })
    }
  }, [history, verifyAndRedirect])

  return (
    <FullPageContainer c>
      <Box c>
        <AnimatedLoader onlyWhite />
      </Box>
    </FullPageContainer>
  )
}

export default TwitterAuth
